import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const SKIP = 'script,style,noscript,code,pre,textarea,[data-no-translate],[contenteditable="true"]';
const ATTRS = ["placeholder", "title", "alt", "aria-label"];
// Only translate real words: 2+ consecutive letters and not an all-caps code/SKU (e.g. "AB-001", "J$1,200", "08:59:12").
const hasLatin = (s) => /[A-Za-z]{2}/.test(s) && !/^[A-Z0-9\s\-_.,:/$#%()]+$/.test(s.trim());
const build = (full, tr) => {
  if (tr === undefined) return undefined;
  const lead = full.match(/^\s*/)[0];
  const trail = full.match(/\s*$/)[0];
  return lead + tr + trail;
};

// Full-site machine translation. Walks the DOM, translates every English text
// node + key attributes via the cached /i18n/translate endpoint, and restores
// originals when English is selected.
export default function AutoTranslate() {
  const { lang } = useLang();
  const location = useLocation();
  const origText = useRef(new WeakMap());
  const origAttr = useRef(new WeakMap());
  const written = useRef(new WeakMap());
  const writtenAttr = useRef(new WeakMap()); // el -> {attr: value we wrote} // node -> last value WE wrote, so React re-renders (t() strings) are never mistaken for originals
  const dict = useRef({});
  const inflight = useRef(new Set());
  const langRef = useRef("en");
  const obs = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    langRef.current = lang;
    restore(); // always start a language switch from the English originals (prevents A→B mixing)
    if (lang === "en") { dict.current = {}; return; }
    try { dict.current = JSON.parse(localStorage.getItem("sn_tr_" + lang) || "{}"); }
    catch { dict.current = {}; }
    schedule(50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (langRef.current !== "en") schedule(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Only wake up for mutations that can introduce untranslated English (new nodes or word changes);
    // countdown ticks / numeric updates never trigger a full-DOM pass. Apply cached strings immediately.
    const relevant = (m) => {
      if (m.type === "childList") return [...m.addedNodes].some((n) => n.nodeType === 1 || (n.nodeType === 3 && hasLatin(n.nodeValue || "")));
      if (m.type === "characterData") return hasLatin(m.target.nodeValue || "");
      return false;
    };
    const o = new MutationObserver((ms) => { if (langRef.current !== "en" && ms.some(relevant)) schedule(0); });
    o.observe(document.body, { childList: true, subtree: true, characterData: true });
    obs.current = o;
    return () => o.disconnect();
  }, []);

  function schedule(ms) { clearTimeout(timer.current); timer.current = setTimeout(run, ms); }
  const pause = () => obs.current && obs.current.disconnect();
  const resume = () => obs.current && obs.current.observe(document.body, { childList: true, subtree: true, characterData: true });

  function textNodes(restoring) {
    const nodes = [];
    const w = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode(n) {
        const p = n.parentElement;
        if (!p || p.closest(SKIP)) return NodeFilter.FILTER_REJECT;
        const v = n.nodeValue;
        if (!v || !v.trim()) return NodeFilter.FILTER_REJECT;
        if (origText.current.has(n)) return NodeFilter.FILTER_ACCEPT;
        if (restoring || !hasLatin(v)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let n; while ((n = w.nextNode())) nodes.push(n);
    return nodes;
  }

  function run() {
    if (langRef.current === "en") return;
    const missing = apply();
    if (missing.size) fetchMissing([...missing]);
  }

  // Applies known translations to the DOM and returns the set of still-missing strings.
  function apply() {
    const d = dict.current;
    const missing = new Set();
    pause();
    for (const node of textNodes(false)) {
      const stored = origText.current.get(node);
      const cur = node.nodeValue;
      let full;
      if (stored === undefined) { full = cur; origText.current.set(node, cur); }
      else if (cur === stored || cur === written.current.get(node)) full = stored; // untouched, or still showing our translation
      else { full = cur; origText.current.set(node, cur); written.current.delete(node); } // React changed it → new original
      const key = full.trim();
      if (!hasLatin(key)) continue;
      const tr = d[key];
      if (tr !== undefined) { const nv = build(full, tr); if (node.nodeValue !== nv) node.nodeValue = nv; written.current.set(node, nv); }
      else if (!inflight.current.has(key)) missing.add(key);
    }
    document.body.querySelectorAll("[placeholder],[title],[alt],[aria-label]").forEach((el) => {
      if (el.closest(SKIP)) return;
      let store = origAttr.current.get(el);
      if (!store) { store = {}; origAttr.current.set(el, store); }
      let wa = writtenAttr.current.get(el);
      if (!wa) { wa = {}; writtenAttr.current.set(el, wa); }
      for (const a of ATTRS) {
        if (!el.hasAttribute(a)) continue;
        const curv = el.getAttribute(a);
        if (!(a in store) || (curv !== store[a] && curv !== wa[a])) { store[a] = curv; delete wa[a]; } // React re-rendered it → new original
        const src = store[a];
        if (!src || !hasLatin(src)) continue;
        const key = src.trim();
        const tr = d[key];
        if (tr !== undefined) { if (el.getAttribute(a) !== tr) el.setAttribute(a, tr); wa[a] = tr; }
        else if (!inflight.current.has(key)) missing.add(key);
      }
    });
    resume();
    return missing;
  }

  async function fetchMissing(list) {
    const lg = langRef.current;
    const CH = 25;
    for (let i = 0; i < list.length; i += CH) {
      const chunk = list.slice(i, i + CH);
      chunk.forEach((s) => inflight.current.add(s));
      try {
        const { data } = await api.post("/i18n/translate", { lang: lg, texts: chunk });
        if (langRef.current !== lg) return;
        Object.assign(dict.current, data.translations || {});
        try { localStorage.setItem("sn_tr_" + lg, JSON.stringify(dict.current)); } catch { /* quota */ }
      } catch { /* offline */ }
      chunk.forEach((s) => inflight.current.delete(s));
      apply();
    }
  }

  function restore() {
    pause();
    for (const node of textNodes(true)) {
      const o = origText.current.get(node);
      // only undo OUR writes; if React already re-rendered the node (t() strings), its value is the truth
      if (o !== undefined && node.nodeValue === written.current.get(node) && node.nodeValue !== o) node.nodeValue = o;
      origText.current.delete(node); written.current.delete(node);
    }
    document.body.querySelectorAll("[placeholder],[title],[alt],[aria-label]").forEach((el) => {
      const store = origAttr.current.get(el); const wa = writtenAttr.current.get(el) || {};
      if (store) for (const a in store) if (store[a] != null && el.getAttribute(a) === wa[a]) el.setAttribute(a, store[a]);
      origAttr.current.delete(el); writtenAttr.current.delete(el);
    });
    resume();
  }

  return null;
}
